import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';
import { withStyles  } from '@mui/styles';
import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Button as ReactButton } from '@mui/material';
import { IconButton as ReactIconButton } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { getGridSetting, setGridSetting } from './utils/storage';
import ColumnSettings from './ColumnSettings';
import GeneralSettings from './GeneralSettings';
import { WithTracking } from "../WithTracking";
const IconButton = WithTracking(ReactIconButton);
const Button = WithTracking(ReactButton);

const styles = theme => ({
  card: {
    width: 400,
    position: 'absolute',
    right: 20,
    zIndex: 1000
  },
  cardHeader: {
    background: theme.palette.primary.main
  },
  cardContent: {
    padding: 0,
    overflowY: 'auto',
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.grey[300]
  },
  view: {
    padding: 20
  },
  white: {
    color: theme.palette.common.white
  },
  right: {
    marginLeft: 'auto'
  }
});

class Settings extends Component {

  constructor(props) {
    super(props);
    const columns = getGridSetting(props.title, 'columns') || props.columns;
    this.cardRef = React.createRef();
    this.cardContentRef = React.createRef();

    this.columns = columns;
    this.state = {
      columns,
      tab: 0,
      settingMode: false,
      showNotification: false
    };
  }

  adjustHeight = () => {
    const card = this.cardRef.current;
    if (!card) return;
    const cardContent = this.cardContentRef.current;
    const { top } = card.getBoundingClientRect();
    const height = window.innerHeight - top - 100;
    card.style.height = `${height}px`;
    cardContent.style.height = `${height-120}px`;
  }

  componentDidMount() {
    window.addEventListener('resize', this.adjustHeight);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.adjustHeight);
  }

  toggleSettings = async () => {
    await this.setState(prevState => ({ settingMode: !prevState.settingMode }));
    this.adjustHeight();
  }

  done = () => this.setState({ settingMode: false });

  /** ****************************************************
   *                  Column Actions
   *************************************************** */
  handleColumnChange = column => evt => {
    const { columns } = this.state;
    const index = columns.findIndex(col => col.id === column.id);
    columns[index].visible = evt.target.checked;
    this.props.onColumnToggle(columns);
  }

  showAllColumns = () => {
    const columns = this.state.columns.map(col => {
      return Object.assign({}, col, { visible: true });
    });

    this.props.onColumnToggle(columns);
    this.columns = columns;
    this.setState({ columns });
  }

  saveColumnSettings = () => {
    setGridSetting(this.props.title, 'columns', this.state.columns);
    this.setState({
      showNotification: true,
      notification: 'Saved columns settings'
    });
  }

  deleteColumnSettings = () => {
    const { title } = this.props;
    const columns = getGridSetting(title, 'columns')
      .map(col => Object.assign(col, { visible: true }));
    setGridSetting(title, 'columns', columns);
    this.showAllColumns();
    this.setState({
      showNotification: true,
      notification: 'Deleted columns settings'
    });
  };

  /* ********** End Column Actions ******************* */

  hideNotification = (/* event, reason */) => {
    this.setState({
      showNotification: false,
      notification: ''
    })
  };

  moveTab = (evt, value) => {
    this.setState({ tab: value });
  };

  changeTabIndex = index => {
    this.setState({ tab: index });
  };

  render() {
    const { tab, columns, settingMode } = this.state;
    const { classes, onSwitch, onDensityChange, onRestore, settings } = this.props;

    return (
      <div>
        <Tooltip title="Settings" placement="top">
          <IconButton
            arial-label="Settings"
            onClick={this.toggleSettings}
            tracking={{action:"Show Grid Settings"}}
            size="large">
            <SettingsIcon />
          </IconButton>
        </Tooltip>
        {settingMode && (
        <ClickAwayListener onClickAway={this.done}>
          <Card className={classes.card} ref={this.cardRef}>
            <CardHeader
              title="Settings"
              className={classes.cardHeader}
              classes={{
                title: classes.white
              }}
            />
            <CardContent className={classes.cardContent} ref={this.cardContentRef}>
              <Tabs
                value={tab}
                onChange={this.moveTab}
                indicatorColor="primary"
                textColor="primary"
                fullWidth
              >
                <Tab label="General" />
                <Tab label="Columns" />
              </Tabs>
              <SwipeableViews
                axis="x"
                index={tab}
              >
                <div className={classes.view}>
                  <GeneralSettings
                    settings={settings}
                    onSwitch={onSwitch}
                    density={settings.density}
                    onRestore={onRestore}
                    onDensityChange={onDensityChange}
                  />
                </div>
                <div>
                  <ColumnSettings
                    columns={columns}
                    onChange={this.handleColumnChange}
                    onSave={this.saveColumnSettings}
                    onDelete={this.deleteColumnSettings}
                    onShowAll={this.showAllColumns}
                  />
                </div>
              </SwipeableViews>
            </CardContent>

            <CardActions>
              <Button
                color="primary"
                className={classes.right}
                onClick={this.done}
                tracking={{action:"Done"}}
              >
                Done
              </Button>
            </CardActions>
          </Card>
        </ClickAwayListener>
        )}
        <Snackbar
          open={this.state.showNotification}
          autoHideDuration={3000}
          onClose={this.hideNotification}
          message={this.state.notification}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
        />
      </div>
    );
  }
}

Settings.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired
};

export default withStyles(styles, { withTheme: true })(Settings);
